/**
 * Seller-facing submit presentation for SellerIntakeForm (Sell Your Car M7).
 */
import { SELLER_SUBMISSION_STATUSES } from "../../../utils/sellerIntake";
import { translateValidationMessage } from "../../../intl/validationMessages.js";
import {
  SELLER_BROWSE_VEHICLES_HREF,
  SELLER_SUBMISSION_RESULT_COPY_ES,
  SELLER_SUBMIT_UI_STATES,
} from "./sellerSubmissionResultConstants";

const HIDE_FORM_STATES = new Set([
  SELLER_SUBMIT_UI_STATES.SUCCESS,
  SELLER_SUBMIT_UI_STATES.DUPLICATE,
]);

const RETRYABLE_STATES = new Set([
  SELLER_SUBMIT_UI_STATES.NETWORK_ERROR,
  SELLER_SUBMIT_UI_STATES.SERVICE_ERROR,
  SELLER_SUBMIT_UI_STATES.UNKNOWN_ERROR,
]);

const RESULT_PANEL_STATES = new Set([
  SELLER_SUBMIT_UI_STATES.SUCCESS,
  SELLER_SUBMIT_UI_STATES.VALIDATION_ERROR,
  SELLER_SUBMIT_UI_STATES.NETWORK_ERROR,
  SELLER_SUBMIT_UI_STATES.SERVICE_ERROR,
  SELLER_SUBMIT_UI_STATES.DUPLICATE,
  SELLER_SUBMIT_UI_STATES.SPAM_REJECTED,
  SELLER_SUBMIT_UI_STATES.RATE_LIMITED,
  SELLER_SUBMIT_UI_STATES.UNKNOWN_ERROR,
]);

/**
 * @param {import('../../../utils/sellerIntake/sellerIntakeTypes').SellerSubmissionResult|null|undefined} result
 * @param {{ clientValidationFailed?: boolean, clientSpam?: boolean }} [options]
 * @returns {string}
 */
export function mapSellerSubmissionResultToSubmitState(result, options = {}) {
  if (options.clientSpam) {
    return SELLER_SUBMIT_UI_STATES.SPAM_REJECTED;
  }

  if (options.clientValidationFailed) {
    return SELLER_SUBMIT_UI_STATES.VALIDATION_ERROR;
  }

  if (!result) {
    return SELLER_SUBMIT_UI_STATES.IDLE;
  }

  if (result.ok || result.status === SELLER_SUBMISSION_STATUSES.RECEIVED) {
    return SELLER_SUBMIT_UI_STATES.SUCCESS;
  }

  switch (result.status) {
    case SELLER_SUBMISSION_STATUSES.DUPLICATE:
      return SELLER_SUBMIT_UI_STATES.DUPLICATE;
    case SELLER_SUBMISSION_STATUSES.RATE_LIMITED:
      return SELLER_SUBMIT_UI_STATES.RATE_LIMITED;
    case SELLER_SUBMISSION_STATUSES.SPAM_REJECTED:
      return SELLER_SUBMIT_UI_STATES.SPAM_REJECTED;
    case SELLER_SUBMISSION_STATUSES.NETWORK_ERROR:
      return SELLER_SUBMIT_UI_STATES.NETWORK_ERROR;
    case SELLER_SUBMISSION_STATUSES.SERVICE_ERROR:
      return SELLER_SUBMIT_UI_STATES.SERVICE_ERROR;
    case SELLER_SUBMISSION_STATUSES.VALIDATION_ERROR:
      return SELLER_SUBMIT_UI_STATES.VALIDATION_ERROR;
    default:
      break;
  }

  if (result.formErrors && Object.keys(result.formErrors).length > 0) {
    return SELLER_SUBMIT_UI_STATES.VALIDATION_ERROR;
  }

  return SELLER_SUBMIT_UI_STATES.UNKNOWN_ERROR;
}

/**
 * @param {{
 *   submitState?: string,
 *   submissionResult?: import('../../../utils/sellerIntake/sellerIntakeTypes').SellerSubmissionResult|null,
 * }} [params]
 */
export function resolveSellerSubmissionSubmitPresentation({
  submitState = SELLER_SUBMIT_UI_STATES.IDLE,
  submissionResult = null,
  photoUploadState = null,
  t = null,
} = {}) {
  const localized = (key, fallback) => (t ? t(key) : fallback);
  const localizedMessage = (message, fallback) => {
    if (!message) return fallback;
    if (t) return translateValidationMessage(t, message);
    return message;
  };
  const isPending = submitState === SELLER_SUBMIT_UI_STATES.SUBMITTING;
  const showForm = !HIDE_FORM_STATES.has(submitState);
  const showResultPanel = RESULT_PANEL_STATES.has(submitState);
  const retryable =
    submissionResult?.retryable === true && RETRYABLE_STATES.has(submitState);
  const showRetry = retryable && !isPending;
  const showSubmitAnother = submitState === SELLER_SUBMIT_UI_STATES.SUCCESS;
  const showSubmit =
    showForm &&
    submitState !== SELLER_SUBMIT_UI_STATES.RATE_LIMITED &&
    (!retryable || isPending);

  /** @type {{ label: string, href: string }[]} */
  const links = [];

  if (submitState === SELLER_SUBMIT_UI_STATES.SUCCESS) {
    links.push({
      label: localized("automotive.browseVehicles", SELLER_SUBMISSION_RESULT_COPY_ES.browseVehicles),
      href: SELLER_BROWSE_VEHICLES_HREF,
    });
  }

  if (submitState === SELLER_SUBMIT_UI_STATES.DUPLICATE) {
    links.push({
      label: localized("automotive.browseVehicles", SELLER_SUBMISSION_RESULT_COPY_ES.browseVehicles),
      href: SELLER_BROWSE_VEHICLES_HREF,
    });
  }

  /** @type {string[]} */
  const steps = [];

  let title = null;
  let body = null;
  /** @type {'success'|'error'|'warning'|null} */
  let variant = null;

  switch (submitState) {
    case SELLER_SUBMIT_UI_STATES.SUCCESS:
      title = localized("automotive.submissionReceived", SELLER_SUBMISSION_RESULT_COPY_ES.successTitle);
      body = localized("automotive.weWillContactYou", SELLER_SUBMISSION_RESULT_COPY_ES.successBody);
      steps.push(
        SELLER_SUBMISSION_RESULT_COPY_ES.successStepReview,
        SELLER_SUBMISSION_RESULT_COPY_ES.successStepContact,
        SELLER_SUBMISSION_RESULT_COPY_ES.successStepNoAutoPublish
      );
      if (photoUploadState?.uploading) {
        steps.push(`Subiendo fotos (${photoUploadState.completed}/${photoUploadState.total})…`);
      } else if (submissionResult?.photoUploadStatus === "success") {
        steps.push(SELLER_SUBMISSION_RESULT_COPY_ES.successPhotosUploaded);
      } else if (submissionResult?.photoUploadStatus === "partial") {
        steps.push(SELLER_SUBMISSION_RESULT_COPY_ES.successPhotosPartial);
      } else if (submissionResult?.photoUploadStatus === "failed") {
        steps.push(SELLER_SUBMISSION_RESULT_COPY_ES.successPhotosPartial);
      } else if (submissionResult?.photoUploadStatus === "skipped") {
        steps.push(SELLER_SUBMISSION_RESULT_COPY_ES.successPhotosSkipped);
      }
      variant = "success";
      break;
    case SELLER_SUBMIT_UI_STATES.VALIDATION_ERROR:
      title = SELLER_SUBMISSION_RESULT_COPY_ES.validationErrorTitle;
      body = SELLER_SUBMISSION_RESULT_COPY_ES.validationErrorBody;
      variant = "error";
      break;
    case SELLER_SUBMIT_UI_STATES.DUPLICATE:
      title = SELLER_SUBMISSION_RESULT_COPY_ES.duplicateTitle;
      body = localizedMessage(
        submissionResult?.sellerMessage,
        SELLER_SUBMISSION_RESULT_COPY_ES.duplicateBody
      );
      variant = "warning";
      break;
    case SELLER_SUBMIT_UI_STATES.RATE_LIMITED:
      title = SELLER_SUBMISSION_RESULT_COPY_ES.rateLimitedTitle;
      body = localizedMessage(
        submissionResult?.sellerMessage,
        SELLER_SUBMISSION_RESULT_COPY_ES.rateLimitedBody
      );
      variant = "warning";
      break;
    case SELLER_SUBMIT_UI_STATES.SPAM_REJECTED:
      title = SELLER_SUBMISSION_RESULT_COPY_ES.spamRejectedTitle;
      body = SELLER_SUBMISSION_RESULT_COPY_ES.spamRejectedBody;
      variant = "error";
      break;
    case SELLER_SUBMIT_UI_STATES.NETWORK_ERROR:
      title = SELLER_SUBMISSION_RESULT_COPY_ES.networkErrorTitle;
      body = localizedMessage(
        submissionResult?.sellerMessage,
        SELLER_SUBMISSION_RESULT_COPY_ES.networkErrorBody
      );
      variant = "error";
      break;
    case SELLER_SUBMIT_UI_STATES.SERVICE_ERROR:
    case SELLER_SUBMIT_UI_STATES.UNKNOWN_ERROR:
      title = SELLER_SUBMISSION_RESULT_COPY_ES.serviceErrorTitle;
      body = localizedMessage(
        submissionResult?.sellerMessage,
        SELLER_SUBMISSION_RESULT_COPY_ES.serviceErrorBody
      );
      variant = "error";
      break;
    default:
      break;
  }

  return {
    submitState,
    pending: isPending,
    showForm,
    showResultPanel,
    showSubmit,
    showRetry,
    showSubmitAnother,
    title,
    body,
    steps,
    variant,
    links,
    retryLabel: localized("common.retry", SELLER_SUBMISSION_RESULT_COPY_ES.retry),
    submitAnotherLabel: SELLER_SUBMISSION_RESULT_COPY_ES.submitAnother,
  };
}
